// ── V141: 반제품 이름 바꾸기 연동 ─────────────────────────────────────────────
// 레시피끼리는 식재료 '이름'으로 이어져 있다 (불고기 타코의 식재료 '불고기' = 반제품 레시피 '불고기').
// 반제품 레시피는 원가를 쓰려고 단가표에도 한 줄(구분 '반제품', 코드 S+숫자)로 들어간다.
// 예전엔 반제품 레시피 이름을 바꾸면(불고기 → 불고기(시제품소스))
//   - 단가표에 새 이름 줄이 하나 더 생기고 옛 줄은 그대로 남았고
//   - 그 반제품을 쓰는 레시피는 계속 옛 이름 → 옛 줄의 옛 원가를 봤다.
// 이제 이름을 바꾸면 쓰는 레시피의 식재료 이름과 단가표 줄 이름을 같이 바꾼다.
// 이미 끊긴 것(옛 줄만 남은 것)은 findBrokenSemiRefs로 찾아 레시피 화면에서 다시 이어 준다.
import { sameIngredientName, normalizeIngredientName, findPriceByName, findPriceIndexByName } from './ingredientMatch';
import { usableG } from './priceYield';   // V152 실사용량 (신선식품 로스 제외)

export const SEMI_CATEGORY = '반제품';
const nameOf = (x) => String((x && x.name) || '').trim();
const hasHangul = (s) => /[가-힣]/.test(String(s || ''));
// 반제품 레시피를 저장할 때 앱이 만든 단가표 줄 (사람이 단가표에 직접 넣은 반제품과 구별)
export const isAutoSemiRow = (p) => !!p && p.category === SEMI_CATEGORY && /^S\d{6,}$/.test(String(p.code || ''));

/**
 * 반제품 이름이 oldName → newName 으로 바뀌었을 때
 *   - 모든 레시피 식재료의 oldName → newName
 *   - 단가표의 반제품 줄: 이름을 newName으로 (newName 줄이 이미 있으면 옛 줄은 뺀다)
 * 다른 것을 가리키는 이름일 수 있으면(같은 옛 이름의 다른 반제품, 같은 이름의 일반 식재료) 건드리지 않는다.
 * @returns {{ categories, prices, touched, usedIn: string[], priceRow: 'renamed'|'removed'|'none' }}
 */
export function renameSemiEverywhere(categories, prices, { oldName, newName, newZh = '', recipeId = null } = {}) {
  const res = { categories, prices, touched: 0, usedIn: [], priceRow: 'none' };
  const o = String(oldName || '').trim();
  const n = String(newName || '').trim();
  if (!o || !n || sameIngredientName(o, n)) return res;
  const all = (categories || []).flatMap(c => (c && c.items) || []);
  if (all.some(r => r && r.id !== recipeId && r.type === 'semi' && sameIngredientName(r.name, o))) return res;
  if ((prices || []).some(p => sameIngredientName(p && p.name, o) && !(p && p.category === SEMI_CATEGORY))) return res;
  const zh = String(newZh || '').trim();

  let touched = 0;
  const usedIn = [];
  const nextCats = (categories || []).map(c => {
    let catChanged = false;
    const items = ((c && c.items) || []).map(item => {
      if (!item || !Array.isArray(item.ingredients)) return item;
      let changed = false;
      const ings = item.ingredients.map(ing => {
        if (!sameIngredientName(ing && ing.name, o)) return ing;
        changed = true;
        touched++;
        const ni = { ...ing, name: n };
        if (Object.prototype.hasOwnProperty.call(ni, 'name_zh')) { if (zh && !hasHangul(zh)) ni.name_zh = zh; else delete ni.name_zh; }
        return ni;
      });
      if (!changed) return item;
      catChanged = true;
      if (!usedIn.includes(nameOf(item))) usedIn.push(nameOf(item));
      return { ...item, ingredients: ings };
    });
    return catChanged ? { ...c, items } : c;
  });

  let ps = prices || [];
  let priceRow = 'none';
  const oi = ps.findIndex(p => sameIngredientName(p && p.name, o) && p.category === SEMI_CATEGORY);
  const ni = findPriceIndexByName(ps, n);
  if (oi >= 0 && ni >= 0 && ni !== oi) {
    ps = ps.filter((_, i) => i !== oi);          // 새 이름 줄이 이미 있음 → 옛 줄(옛 원가)은 정리
    priceRow = 'removed';
  } else if (oi >= 0) {
    ps = ps.map((p, i) => {
      if (i !== oi) return p;
      const q = { ...p, name: n };
      if (zh && !hasHangul(zh)) q.name_zh = zh; else delete q.name_zh;   // 옛 이름의 중국어는 틀린 번역
      return q;
    });
    priceRow = 'renamed';
  }
  return { categories: touched ? nextCats : categories, prices: ps, touched, usedIn, priceRow };
}

/**
 * 반제품 레시피 원가를 단가표 줄에 반영 (없으면 줄을 만든다) — 예전 App.handleSaveRecipe 안의 계산 그대로
 */
export function syncSemiPrice(prices, item, { now = Date.now() } = {}) {
  const ps = prices || [];
  const raw = ((item && item.ingredients) || []).reduce((sum, ing) => {
    const p = findPriceByName(ps, ing.name);
    if (!p || !usableG(p)) return sum;
    return sum + (ing.qty / usableG(p)) * p.price;   // V152 실사용량 기준
  }, 0);
  // V125: 위안처럼 작은 금액은 소수 둘째 자리까지
  const batchCost = raw >= 100 ? Math.round(raw) : Math.round(raw * 100) / 100;
  // V153: 반제품 줄(구분 '반제품')만 고친다. 예전엔 이름이 같은 첫 줄을 덮어써서,
  //       반제품 '나쵸치즈'를 저장하면 식재료 '나쵸치즈' 줄이 반제품 원가로 바뀌어 버렸다.
  //       이름이 같은 식재료 줄만 있으면 건드리지 않는다 (이름 겹침 — semiClash.js에서 고친다)
  const idx = ps.findIndex(p => p && p.category === SEMI_CATEGORY && sameIngredientName(p.name, item.name));
  if (idx < 0 && findPriceIndexByName(ps, item.name) >= 0) return ps;
  if (idx >= 0) {
    return ps.map((p, i) => (i === idx
      ? { ...p, category: SEMI_CATEGORY, price: batchCost, net_g: item.total_output || p.net_g,
          ...(item.name_zh && !p.name_zh ? { name_zh: item.name_zh } : {}) }
      : p));
  }
  return [...ps, {
    code: 'S' + now, name: item.name,
    spec: `${item.total_output}g/배치`, unit: 'g',
    price: batchCost, net_g: item.total_output || 0, category: SEMI_CATEGORY,
    ...(item.name_zh ? { name_zh: item.name_zh } : {}),
  }];
}

/**
 * 이미 끊긴 연결 찾기 — 레시피 식재료 이름이 '앱이 만든 반제품 줄'에만 있고 그 이름의 반제품 레시피는 없는 것
 * (반제품 이름을 바꾼 뒤 옛 이름이 남은 경우, 반제품 레시피를 지운 경우)
 * @returns Map<정규화 이름, { name, usedIn: string[], candidates: [{ id, name, name_zh, score }] }>
 */
export function findBrokenSemiRefs(recipes, prices) {
  const all = (recipes || []).filter(Boolean);
  const semis = all.filter(r => r.type === 'semi' && nameOf(r));
  const semiKeys = new Set(semis.map(s => normalizeIngredientName(s.name)));
  const out = new Map();
  for (const r of all) {
    for (const ing of r.ingredients || []) {
      const nm = nameOf(ing);
      const key = normalizeIngredientName(nm);
      if (!key || semiKeys.has(key)) continue;
      const rows = (prices || []).filter(p => normalizeIngredientName(p && p.name) === key);
      if (!rows.length || !rows.every(isAutoSemiRow)) continue;
      const e = out.get(key) || { name: nm, usedIn: [], candidates: [] };
      if (!e.usedIn.includes(nameOf(r))) e.usedIn.push(nameOf(r));
      out.set(key, e);
    }
  }
  for (const [key, e] of out) {
    const score = (s) => {
      const sk = normalizeIngredientName(s.name);
      return sk.startsWith(key) ? 0 : sk.includes(key) ? 1 : key.includes(sk) ? 2 : 3;
    };
    e.candidates = semis
      .map(s => ({ id: s.id, name: nameOf(s), name_zh: s.name_zh || '', score: score(s) }))
      .sort((a, b) => a.score - b.score || a.name.localeCompare(b.name, 'ko'));
  }
  return out;
}
